import { Award, Check, HelpCircle, RefreshCw, Search, Target, Users, X } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { authAxios } from "../../utils/auth";
import { toast } from "sonner";

const Row = ({ label, current, needed, testId }) => {
  const met = Number(current) >= Number(needed);
  const pct = Math.min(100, needed > 0 ? Math.round((current / needed) * 100) : 100);
  return (
    <div className="space-y-1" data-testid={testId}>
      <div className="flex items-center justify-between text-xs">
        <span className="text-white/70">{label}</span>
        <span className={`font-mono ${met ? "text-emerald-300" : "text-white/50"}`}>
          {current} / {needed}
        </span>
      </div>
      <div className="h-1.5 rounded-full bg-white/[0.05] overflow-hidden">
        <div
          className={`h-full transition-all ${met ? "bg-emerald-400/70" : "bg-amber-400/70"}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

const Pill = ({ qualified, label, testId }) => (
  <span
    data-testid={testId}
    className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full border font-medium ${
      qualified
        ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
        : "border-white/10 text-white/50 bg-white/[0.02]"
    }`}
  >
    {qualified ? <Check className="w-2.5 h-2.5" /> : <Target className="w-2.5 h-2.5" />}
    {qualified ? "Qualified" : "Not yet"} · {label}
  </span>
);

export default function QualificationCard({ qualification, onRefresh }) {
  const navigate = useNavigate();
  const [refreshing, setRefreshing] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await authAxios.post("/affiliate/qualification/refresh");
      if (typeof onRefresh === "function") onRefresh(res.data);
      toast.success("Qualification recomputed");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (e?.message || "Failed to refresh");
      toast.error(msg);
    } finally {
      setRefreshing(false);
    }
  };

  // Audit now opens the full three-stream self-audit page (Audit v2 · self mode).
  // Replaces the old 180d /affiliate/audit toast summary which showed opaque
  // OK/Missing/Mismatch counts with no context, no cross-reference against
  // Stripe balance, and no plain-English verdict.
  const handleOpenAudit = () => {
    navigate("/affiliate?tab=audit");
  };

  if (!qualification) return null;
  const profile = qualification.compensation_profile || "STANDARD";
  const isPro = profile === "PRO";
  const snap = qualification.snapshot || {};
  const sponsor = qualification.sponsor_commission || {};
  const leadership = qualification.leadership_commission || {};

  return (
    <section
      data-testid="qualification-card"
      className="rounded-xl border border-white/[0.06] bg-[#111113] p-5"
    >
      {/* Header — title on top, actions below (mobile-friendly: audit /
          refresh / period no longer collide with the title on narrow
          screens). Actions still hug the right edge from sm+ via flex-wrap. */}
      <div className="mb-4 space-y-2">
        <div className="flex items-center gap-2">
          <Award className="w-4 h-4 text-[#C5A55A]" />
          <h2 className="text-sm font-semibold text-white/60 uppercase tracking-wider">
            Compensation
          </h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={handleOpenAudit}
            title="Open the full three-stream Commission Audit — Stripe payouts, commissions, and coin account reconciliation"
            data-testid="qualification-audit-btn"
            className="flex items-center gap-1 px-2 py-0.5 rounded-md border border-emerald-500/30 hover:border-emerald-500/60 text-[10px] text-emerald-300 hover:text-emerald-200 transition"
          >
            <Search className="w-3 h-3" />
            Audit
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            title="Force recompute (bypass 15-min cache)"
            data-testid="qualification-refresh-btn"
            className="flex items-center gap-1 px-2 py-0.5 rounded-md border border-white/10 hover:border-white/20 text-[10px] text-white/60 hover:text-white disabled:opacity-40 transition"
          >
            <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "…" : "Refresh"}
          </button>
          <span className="text-[10px] font-mono text-white/30">
            {snap.qualification_period || "—"}
          </span>
        </div>
      </div>

      {/* Audit result summary removed — the Audit button now opens the full
          three-stream self-audit at /affiliate?tab=audit which cross-references
          Stripe payouts, commissions, and the coin account with plain-English
          verdicts per card. */}

      {/* ── DIRECT CUSTOMER RATE (top card) ────────────────────────────── */}
      {isPro ? (
        <div
          data-testid="pro-direct-card"
          className="rounded-lg border border-[#C5A55A]/30 bg-gradient-to-br from-[#C5A55A]/10 to-transparent p-4 mb-5"
        >
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-[#C5A55A]/40 bg-[#C5A55A]/10 text-[#C5A55A] text-[10px] font-semibold uppercase tracking-wider">
                PRO
              </span>
              <span className="text-white/60 text-xs">Your direct-customer rate</span>
            </div>
            <div className="text-2xl font-mono font-bold text-[#C5A55A]">
              {(qualification.direct_customer_rate * 100).toFixed(0)}%
            </div>
          </div>
          <p className="text-[11px] text-white/50 leading-relaxed">
            When someone signs up under <em>your</em> link and buys, you earn a flat 30%. On
            your direct sales, no Sponsor or Leadership overrides are paid above you — the
            transaction stops with you.
          </p>
        </div>
      ) : (
        <div
          data-testid="standard-direct-card"
          className="rounded-lg border border-emerald-500/20 bg-emerald-500/[0.05] p-4 mb-5"
        >
          <div className="flex items-center justify-between mb-1.5">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-white/10 text-white/60 text-[10px] font-semibold uppercase tracking-wider">
                STANDARD
              </span>
              <span className="text-white/60 text-xs">Your direct-customer rate</span>
            </div>
            <div className="text-2xl font-mono font-bold text-emerald-300">
              {(qualification.direct_customer_rate * 100).toFixed(0)}%
            </div>
          </div>
          <p className="text-[11px] text-white/50 leading-relaxed">
            You earn 20% on every purchase your direct customers make. Sponsor (6%) and
            Leadership (4%) overrides flow up your chain if those affiliates are qualified.
          </p>
        </div>
      )}

      {/* ── UPLINE COMMISSIONS (apply to BOTH STANDARD and PRO) ────────── */}
      <div className="flex items-center gap-2 mb-3">
        <Users className="w-3.5 h-3.5 text-white/40" />
        <h3 className="text-xs font-semibold text-white/60 uppercase tracking-wider">
          Earn More by Growing Your Customer Referrals
        </h3>
      </div>
      <p className="text-[11px] text-white/40 mb-4 leading-relaxed">
        Your customers can become active affiliates by referring new customers to Agent Eros.
        When they generate customer sales, you can earn a{" "}
        <span className="text-sky-300">6% Sponsor Commission</span> on those sales.
        <br />
        If those affiliates then develop other affiliates who generate customer sales, you can
        earn a <span className="text-indigo-300">4% Leadership Commission</span> on those sales.
      </p>

      {/* Rate mini-table */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        <div className="rounded-lg bg-sky-500/[0.05] ring-1 ring-sky-500/20 p-3 text-center">
          <div className="text-[10px] uppercase tracking-wider text-white/50 mb-1">Sponsor</div>
          <div className="text-lg font-mono font-semibold text-sky-300">
            {(sponsor.rate * 100).toFixed(0)}%
          </div>
          <div className="text-[10px] text-white/30 mt-0.5">
            {sponsor.qualified ? "Earning" : "Locked"}
          </div>
        </div>
        <div className="rounded-lg bg-indigo-500/[0.05] ring-1 ring-indigo-500/20 p-3 text-center">
          <div className="text-[10px] uppercase tracking-wider text-white/50 mb-1">Leadership</div>
          <div className="text-lg font-mono font-semibold text-indigo-300">
            {(leadership.rate * 100).toFixed(0)}%
          </div>
          <div className="text-[10px] text-white/30 mt-0.5">
            {leadership.qualified ? "Earning" : "Locked"}
          </div>
        </div>
      </div>

      {/* Sponsor progress */}
      <div className="space-y-3 pb-4 border-b border-white/5">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-white/70">
            Unlock 6% Sponsor Commission
          </span>
          <Pill qualified={!!sponsor.qualified} label="Sponsor" testId="pill-sponsor" />
        </div>
        <p className="text-[11px] text-white/40 leading-relaxed -mt-1">
          Earn 6% Sponsor Commission on customer sales generated by affiliates you&apos;ve
          directly referred.
        </p>
        <Row
          label="Personal active customers"
          current={snap.personal_active_customer_count || 0}
          needed={sponsor.min_personal_customers || 3}
          testId="row-sponsor-customers"
        />
        <Row
          label="Productive direct affiliates"
          current={snap.productive_direct_affiliate_count || 0}
          needed={sponsor.min_productive_direct_affiliates || 1}
          testId="row-sponsor-affiliates"
        />
      </div>

      {/* Leadership progress */}
      <div className="space-y-3 pt-4">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold text-white/70">
            Unlock 4% Leadership Commission
          </span>
          <Pill qualified={!!leadership.qualified} label="Leadership" testId="pill-leadership" />
        </div>
        <p className="text-[11px] text-white/40 leading-relaxed -mt-1">
          Earn 4% Leadership Commission on customer sales generated by the next generation
          of affiliates developed through your direct affiliates.
        </p>
        <Row
          label="Personal active customers"
          current={snap.personal_active_customer_count || 0}
          needed={leadership.min_personal_customers || 6}
          testId="row-leadership-customers"
        />
        <Row
          label="Productive direct affiliates"
          current={snap.productive_direct_affiliate_count || 0}
          needed={leadership.min_productive_direct_affiliates || 2}
          testId="row-leadership-affiliates"
        />
        <Row
          label="Directs with 3+ customers"
          current={snap.qualifying_direct_affiliate_count || 0}
          needed={2}
          testId="row-leadership-qualifying"
        />
        <Row
          label="Productive 2nd-generation affiliates"
          current={snap.productive_second_generation_count || 0}
          needed={leadership.min_productive_second_generation_affiliates || 1}
          testId="row-leadership-second-gen"
        />
      </div>

      <p className="text-[11px] text-white/40 mt-4 leading-relaxed">
        An <em>active customer</em> is a direct referral on a paid plan, or a customer who
        has made a qualifying purchase within the last 30 days. Qualification progress is
        recalculated monthly.
      </p>

      {/* Audit help modal — explains what the four counters + delta mean */}
      {showHelp && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setShowHelp(false)}
          data-testid="audit-help-modal"
        >
          <div
            className="relative w-full max-w-lg rounded-xl border border-emerald-500/30 bg-[#111113] p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowHelp(false)}
              className="absolute top-3 right-3 text-white/40 hover:text-white"
              aria-label="Close"
              data-testid="audit-help-close"
            >
              <X className="w-4 h-4" />
            </button>
            <h3 className="text-sm font-semibold text-emerald-300 flex items-center gap-2 mb-3">
              <HelpCircle className="w-4 h-4" /> About this audit
            </h3>
            <p className="text-[12px] text-white/70 leading-relaxed mb-4">
              The audit cross-checks every customer and sub-affiliate payment in the last 180 days
              against what you actually earned in your commission ledger, using the
              qualification history log to know whether you were qualified for a
              6% Sponsor or 4% Leadership override at the moment each payment landed.
            </p>
            <div className="space-y-2 text-[12px]">
              <div className="flex items-start gap-2">
                <span className="mt-0.5 inline-block w-14 text-center rounded bg-emerald-500/15 text-emerald-300 font-mono py-0.5">OK</span>
                <span className="text-white/70">Expected commission matches what was logged.</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="mt-0.5 inline-block w-14 text-center rounded bg-red-500/15 text-red-300 font-mono py-0.5">Missing</span>
                <span className="text-white/70">You should have earned a commission, but the ledger has nothing. Flag support.</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="mt-0.5 inline-block w-14 text-center rounded bg-amber-500/15 text-amber-300 font-mono py-0.5">Mismatch</span>
                <span className="text-white/70">Logged amount differs from expected (partial credit, rate change, or refund).</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="mt-0.5 inline-block w-14 text-center rounded bg-white/10 text-white/50 font-mono py-0.5">NQ</span>
                <span className="text-white/70">
                  <em>Not Qualified.</em> You weren&apos;t at the Sponsor / Leadership
                  threshold when this payment landed, so $0 is the correct outcome —
                  not a bug.
                </span>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-white/10 text-[12px] text-white/70">
              <div><span className="text-white/50">Expected</span> — total you should have earned in the window.</div>
              <div><span className="text-white/50">Logged</span> — total actually recorded in your ledger.</div>
              <div>
                <span className="text-white/50">Delta</span> — <span className="font-mono">logged − expected</span>.
                A clean audit shows <span className="text-emerald-300">$0.00</span> here.
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
